import React, { useState, useEffect } from 'react';
import { Button } from './button';
import { Label } from './label';
import { Input } from './input';
import { Card, CardContent } from './card';
import { SelectWithAdd } from './select-with-add';
import { X, ChevronDown, Search, Plus } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

interface DependentSelectProps {
  label: string;
  parentValue?: string;
  childValue?: string;
  onParentChange: (value: string | string[]) => void;
  onChildChange: (value: string | string[]) => void;
  parentCodelistType: string;
  childCodelistType: string;
  parentLabel?: string;
  childLabel?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  preserveChildOnAutoSelect?: boolean; // Don't reset child when parent is auto-selected
}

interface CodelistItem {
  id?: number;
  code: string;
  name: string;
  description?: string;
  parent_code?: string;
  sort_order?: number;
  is_active?: boolean;
  is_system?: boolean;
}

interface CodelistConfig {
  allow_add: boolean;
  required: boolean;
  multiple: boolean;
  searchable: boolean;
}

export const DependentSelect: React.FC<DependentSelectProps> = ({
  label,
  parentValue = '',
  childValue = '',
  onParentChange,
  onChildChange,
  parentCodelistType,
  childCodelistType,
  parentLabel = 'Category',
  childLabel = 'Type',
  placeholder = 'Select option...',
  required = false,
  disabled = false,
  className = '',
  preserveChildOnAutoSelect = false
}) => {
  const [allChildItems, setAllChildItems] = useState<CodelistItem[]>([]);
  const [childConfig, setChildConfig] = useState<CodelistConfig>({
    allow_add: false,
    required: false,
    multiple: false,
    searchable: true
  });
  const [loading, setLoading] = useState(false);
  const [isAutoSelectingParent, setIsAutoSelectingParent] = useState(false);

  // Load all child items and config on mount
  useEffect(() => {
    loadChildData();
  }, [childCodelistType]);

  const loadChildData = async () => {
    try {
      setLoading(true);
      
      // Load child items
      const response = await api.get<{ success: boolean; data: CodelistItem[] }>(`/codelists/${childCodelistType}`);
      if (response.success) {
        setAllChildItems(response.data);
      }

      // Load child codelist configuration
      const configResponse = await fetch(`/api/codelists/types`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (configResponse.ok) {
        const configData = await configResponse.json();
        const typeConfig = configData.data?.find((t: any) => t.type === childCodelistType);
        if (typeConfig?.settings) {
          setChildConfig(typeConfig.settings);
        }
      }
    } catch (error) {
      console.error('Failed to load child data:', error);
      setAllChildItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleParentChange = (value: string | string[]) => {
    const stringValue = Array.isArray(value) ? value[0] || '' : value;
    
    console.log('=== DependentSelect handleParentChange ===');
    console.log('New parent value:', stringValue);
    console.log('Current child value:', childValue);
    console.log('isAutoSelectingParent:', isAutoSelectingParent);
    console.log('preserveChildOnAutoSelect:', preserveChildOnAutoSelect);
    
    onParentChange(stringValue);
    
    // If child value is no longer valid for the new parent, clear it
    // But don't clear if this is an auto-selection and preserveChildOnAutoSelect is true
    const shouldClearChild = childValue && stringValue && !(preserveChildOnAutoSelect && isAutoSelectingParent);
    console.log('Should clear child?', shouldClearChild);
    
    if (shouldClearChild) {
      const childItem = allChildItems.find(item => item.code === childValue);
      console.log('Child item found:', childItem);
      if (childItem && childItem.parent_code !== stringValue) {
        console.log('Clearing child because parent_code mismatch:', childItem.parent_code, '!==', stringValue);
        onChildChange('');
      }
    }
    
    // Reset auto-selection flag
    setIsAutoSelectingParent(false);
  };

  const handleChildChange = (value: string | string[]) => {
    const stringValue = Array.isArray(value) ? value[0] || '' : value;
    
    console.log('=== DependentSelect handleChildChange ===');
    console.log('New child value:', stringValue);
    console.log('Current parent value:', parentValue);
    
    onChildChange(stringValue);
    
    // If no parent is selected but child is selected, auto-select the parent
    if (stringValue && !parentValue) {
      const childItem = allChildItems.find(item => item.code === stringValue);
      console.log('Child item for auto-parent selection:', childItem);
      if (childItem && childItem.parent_code) {
        console.log('Setting isAutoSelectingParent = true and calling onParentChange with:', childItem.parent_code);
        setIsAutoSelectingParent(true); // Set flag before auto-selecting parent
        onParentChange(childItem.parent_code);
      }
    }
  };

  const handleClearParent = () => {
    onParentChange('');
    // Don't clear child - let user keep their selection and see all options
  };

  // Custom add handler for child items - requires parent to be selected
  const handleChildAdd = async (item: { code: string; name: string; description?: string }) => {
    if (!parentValue) {
      throw new Error(`Please select a ${parentLabel.toLowerCase()} first before adding a new ${childLabel.toLowerCase()}`);
    }

    const response = await fetch('/api/codelists/items', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
      },
      body: JSON.stringify({
        codelist_type: childCodelistType,
        parent_code: parentValue, // Set parent_code for the new item
        ...item
      })
    });

    if (!response.ok) {
      throw new Error('Failed to add item');
    }

    // Reload child data to get the new item
    await loadChildData();
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <Label className="text-base font-medium">{label}</Label>
      
      <div className="flex gap-3 items-end">
        {/* Clear button */}
        <div className="flex flex-col">
          <div className="h-5 mb-2"></div> {/* Spacer to align with labels */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleClearParent}
            disabled={disabled || !parentValue}
            className="h-10 w-10 p-0"
            title="Clear category selection"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Parent selector */}
        <div className="flex-1">
          <SelectWithAdd
            label={parentLabel}
            value={parentValue}
            onChange={handleParentChange}
            codelistType={parentCodelistType}
            placeholder={`Select ${parentLabel.toLowerCase()}...`}
            required={required}
            disabled={disabled}
          />
        </div>

        {/* Child selector - now using SelectWithAdd with filtering */}
        <div className="flex-1">
          <DependentSelectWithAdd
            label={`${childLabel}${parentValue ? ' (filtered by ' + parentLabel.toLowerCase() + ')' : ''}`}
            value={childValue}
            onChange={handleChildChange}
            codelistType={childCodelistType}
            placeholder={`Select ${childLabel.toLowerCase()}...`}
            required={required}
            disabled={disabled || loading}
            allItems={allChildItems}
            config={childConfig}
            parentValue={parentValue}
            parentLabel={parentLabel}
            childLabel={childLabel}
            onAddItem={handleChildAdd}
          />
        </div>
      </div>

      {/* Info text */}
      <div className="text-xs text-muted-foreground">
        {!parentValue && childValue && (
          <p>💡 Category was automatically selected based on your type choice</p>
        )}
        {parentValue && allChildItems.filter(item => item.parent_code === parentValue).length > 0 && (
          <p>Showing {allChildItems.filter(item => item.parent_code === parentValue).length} {childLabel.toLowerCase()}(s) in selected category</p>
        )}
        {!parentValue && !childValue && (
          <p>Select a category to filter types, or select a type to auto-select category</p>
        )}
        {childConfig.allow_add && !parentValue && (
          <p>⚠️ Select a {parentLabel.toLowerCase()} to enable adding new {childLabel.toLowerCase()}s</p>
        )}
      </div>
    </div>
  );
};

// Internal component for dependent child select with filtering
interface DependentSelectWithAddProps {
  label: string;
  value?: string;
  onChange: (value: string) => void;
  codelistType: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  allItems: CodelistItem[];
  config: CodelistConfig;
  parentValue?: string;
  parentLabel: string;
  childLabel: string;
  onAddItem?: (item: { code: string; name: string; description?: string }) => Promise<void>;
}

const DependentSelectWithAdd: React.FC<DependentSelectWithAddProps> = ({
  label,
  value,
  onChange,
  codelistType,
  placeholder = "Select option...",
  disabled = false,
  required = false,
  allItems,
  config,
  parentValue,
  parentLabel,
  childLabel,
  onAddItem
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState({ code: '', name: '', description: '' });
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  // Generate unique code from name
  const generateCodeFromName = (name: string): string => {
    // Convert to lowercase and replace non-ASCII letters and non-digits with underscores
    let baseCode = name
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '_')
      .replace(/_+/g, '_') // Replace multiple underscores with single
      .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
    
    if (!baseCode) {
      baseCode = 'new_item';
    }

    // Check if code is unique
    let finalCode = baseCode;
    let counter = 1;
    
    while (allItems.some(item => item.code === finalCode)) {
      finalCode = `${baseCode}_${counter}`;
      counter++;
    }
    
    return finalCode;
  };

  // Filter items based on parent selection
  const filteredByParent = parentValue 
    ? allItems.filter(item => item.parent_code === parentValue)
    : allItems;

  // Further filter by search term
  const filteredItems = filteredByParent.filter(item => 
    !searchTerm || 
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowAddForm(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (item: CodelistItem) => {
    onChange(item.code);
    setIsOpen(false);
  };

  const handleShowAddForm = () => {
    // Pre-fill form with search term and generated code
    const name = searchTerm.trim() || '';
    const code = name ? generateCodeFromName(name) : '';
    
    setNewItem({ 
      code, 
      name, 
      description: '' 
    });
    setShowAddForm(true);
  };

  const handleAddNew = async () => {
    if (!newItem.code.trim() || !newItem.name.trim()) {
      toast({
        title: "Error",
        description: "Code and name are required",
        variant: "destructive"
      });
      return;
    }

    try {
      if (onAddItem) {
        await onAddItem(newItem);
      }
      
      // Select the newly added item
      onChange(newItem.code);
      
      // Reset form
      setNewItem({ code: '', name: '', description: '' });
      setShowAddForm(false);
      
      toast({
        title: "Success",
        description: `${childLabel} added successfully`
      });
    } catch (error: any) {
      console.error('Failed to add item:', error);
      toast({
        title: "Error",
        description: error.message || `Failed to add ${childLabel.toLowerCase()}`,
        variant: "destructive"
      });
    }
  };

  // Auto-generate code when name changes
  const handleNameChange = (name: string) => {
    setNewItem(prev => ({
      ...prev,
      name,
      code: name ? generateCodeFromName(name) : ''
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && filteredItems.length > 0 && !showAddForm) {
      e.preventDefault();
      handleSelect(filteredItems[0]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setShowAddForm(false);
    }
  };

  const getDisplayValue = () => {
    if (value) {
      const item = allItems.find(i => i.code === value);
      return item ? item.name : value;
    }
    return placeholder;
  };

  // Check if adding is allowed (requires parent to be selected)
  const canAdd = config.allow_add && parentValue;

  return (
    <div className="relative" ref={dropdownRef}>
      <Label className={required ? "after:content-['*'] after:ml-1 after:text-red-500" : ""}>
        {label}
      </Label>

      {/* Main Select Button */}
      <Button
        type="button"
        variant="outline"
        className="w-full justify-between"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
      >
        <span className="truncate">{getDisplayValue()}</span>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </Button>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <Card className="absolute top-full left-0 right-0 z-50 mt-1 max-h-96 overflow-hidden">
          <CardContent className="p-0">
            {/* Search Input */}
            {config.searchable && (
              <div className="p-3 border-b">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="pl-9"
                    autoFocus
                  />
                </div>
              </div>
            )}

            {/* Items List */}
            <div className={`overflow-y-auto ${showAddForm ? 'max-h-32' : 'max-h-48'}`}>
              {filteredItems.length > 0 ? (
                filteredItems.map(item => {
                  const isSelected = value === item.code;
                    
                  return (
                    <div
                      key={item.id || item.code}
                      className={`p-3 cursor-pointer hover:bg-muted border-b last:border-b-0 ${
                        isSelected ? 'bg-muted' : ''
                      }`}
                      onClick={() => handleSelect(item)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-medium">{item.name}</div>
                          <div className="text-sm text-muted-foreground">{item.code}</div>
                          {item.description && (
                            <div className="text-xs text-muted-foreground mt-1">{item.description}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="p-3 text-center text-muted-foreground">
                  {searchTerm ? 'No items found' : 
                   parentValue ? `No ${childLabel.toLowerCase()}s in selected ${parentLabel.toLowerCase()}` :
                   'No items available'}
                </div>
              )}
            </div>

            {/* Add New Item Form */}
            {canAdd && (
              <div className="border-t">
                {!showAddForm ? (
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full justify-start"
                    onClick={handleShowAddForm}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add new {childLabel.toLowerCase()}
                    {searchTerm && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        "{searchTerm}"
                      </span>
                    )}
                  </Button>
                ) : (
                  <div className="p-4 space-y-3">
                    <div className="text-sm font-medium text-muted-foreground">
                      Add new {childLabel.toLowerCase()}
                    </div>
                    <Input
                      placeholder="Code (auto-generated)"
                      value={newItem.code}
                      onChange={(e) => setNewItem(prev => ({ ...prev, code: e.target.value }))}
                    />
                    <Input
                      placeholder="Name"
                      value={newItem.name}
                      onChange={(e) => handleNameChange(e.target.value)}
                    />
                    <Input
                      placeholder="Description (optional)"
                      value={newItem.description}
                      onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
                    />
                    <div className="flex gap-2 pt-2">
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleAddNew}
                        disabled={!newItem.code.trim() || !newItem.name.trim()}
                        className="flex-1"
                      >
                        Add
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setShowAddForm(false);
                          setNewItem({ code: '', name: '', description: '' });
                        }}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Warning when can't add */}
            {config.allow_add && !parentValue && (
              <div className="border-t p-3 text-xs text-muted-foreground bg-muted/50">
                ⚠️ Select a {parentLabel.toLowerCase()} to enable adding new {childLabel.toLowerCase()}s
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
